import {
    Component,
    Object3D,
    PhysXComponent,
    Property,
    TextComponent,
} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {CursorTarget} from '@wonderlandengine/components';
import {AudioSource} from '@wonderlandengine/spatial-audio';
import {PaintballClient} from './paintball-client.js';
import {ShootingTarget} from './shooting-target.js';

/**
 * start-game-button
 */
export class TargetShootingPanel extends Component {
    static TypeName = 'target-shooting-panel';

    static targetShots = 0;
    static userShots = 0;
    static timer = 60;

    targetShots = 0;
    userShots = 0;
    timer = 60;

    audioYouWin!: AudioSource;
    audioYouLoose!: AudioSource;
    bg!: AudioSource;

    /* Properties that are configurable in the editor */

    @property.object({required: true})
    button!: Object3D;

    @property.float(15)
    score!: number;

    @property.object()
    player!: Object3D;

    @property.float(30)
    maxTime!: number;

    @property.object()
    mainBoard!: Object3D;

    @property.object()
    shootingTargets!: Object3D;

    targetShootingInProgress = false;

    buttonTextComp: TextComponent | null = null;
    timerTextComp: TextComponent | null = null;
    accuracyTextComp: TextComponent | null = null;
    scoreTextComp: TextComponent | null = null;

    onActivate(): void {
        this.components = [];
        this.cacheComponents(this.shootingTargets);
        this.components.forEach((c) => {
            c.setActiveHeigherarchy(c.object, false);
        });
    }

    private _isPlayerAlone: boolean = false;

    // Getter for isPlayerAlone
    get isPlayerAlone(): boolean {
        return this._isPlayerAlone;
    }

    // Setter for isPlayerAlone
    set isPlayerAlone(value: boolean) {
        if (this._isPlayerAlone !== value) {
            this._isPlayerAlone = value;
            this.onPlayerAloneStateChange(value); // Trigger function on change
        }
    }

    // Function triggered when isPlayerAlone changes
    private onPlayerAloneStateChange(isAlone: boolean): void {
        if (isAlone) {
            // Perform actions when the player is alone
            this.switchToPanel();
            //spawn all the targets

            this.components.forEach((c) => {
                c.spawn();
            });
        } else {
            // Perform actions when other players join

            this.audioYouWin.play();
            this.resetPlayer();
            this.stopTargetShooting();

            this.switchToLeaderboard();
            //explode all the targets
            this.components.forEach((c) => {
                c.explode();
            });
            this.resetPlayer();
        }
    }

    switchToLeaderboard() {
        this.mainBoard.resetPosition();
        this.object.setPositionWorld([0, -10, 0]);
    }

    switchToPanel() {
        this.object.resetPosition();
        this.mainBoard.setPositionWorld([0, -10, 0]);
    }

    components: ShootingTarget[] = [];
    cacheComponents(obj: Object3D) {
        const comps: ShootingTarget[] = obj
            .getComponents()
            .filter((c) => c.type === 'shooting-target');
        this.components = this.components.concat(comps);
        let children = obj.children;
        if (children)
            for (let i = 0; i < children.length; ++i) {
                this.cacheComponents(children[i]);
            }
    }

    start() {
        this.cacheComponents(this.shootingTargets);
        this.audioYouWin = this.player.addComponent(AudioSource, {
            src: 'sfx/level-up.mp3',
            volume: 0.6,
        })!;

        this.audioYouLoose = this.player.addComponent(AudioSource, {
            src: 'sfx/got-hit.mp3',
            volume: 0.6,
        })!;

        this.bg = this.player.getComponent(PaintballClient)!.audioMusic;

        this.button.getComponent(CursorTarget)!.onClick.add(this.onClick.bind(this));

        this.buttonTextComp =
            this.button!.findByNameRecursive('Text')[0].getComponent(TextComponent)!;

        this.timerTextComp =
            this.object!.findByNameRecursive('Timer')[0].getComponent(TextComponent)!;

        this.accuracyTextComp =
            this.object!.findByNameRecursive('Accuracy')[0].getComponent(TextComponent)!;

        this.scoreTextComp =
            this.object!.findByNameRecursive('Score')[0].getComponent(TextComponent)!;
    }

    onClick() {
        this.buttonTextComp =
            this.button!.findByNameRecursive('Text')[0].getComponent(TextComponent)!;

        if (!this.buttonTextComp) return;
        if (this.buttonTextComp.text === 'Start') this.startTargetShooting();
        else this.stopTargetShooting();
    }

    startTargetShooting() {
        this.buttonTextComp!.text = 'Stop';

        this.bg.play();

        //reset target shots

        this.targetShots = 0;
        this.userShots = 0;
        this.timer = this.maxTime;

        TargetShootingPanel.targetShots = 0;
        TargetShootingPanel.userShots = 0;
        TargetShootingPanel.timer = this.maxTime;

        this.targetShootingInProgress = true;

        this.components.forEach((c) => {
            c.targetShootingInProgress = true;
            c.spawn();
        });
    }

    stopTargetShooting() {
        this.bg.stop();
        this.buttonTextComp!.text = 'Start';

        this.targetShootingInProgress = false;
        this.components.forEach((c) => {
            c.targetShootingInProgress = false;
            c.spawn();
        });
    }

    resetPlayer() {
        const phyxObj = this.player.findByName('PlayerCollider')[0];
        if (phyxObj) {
            phyxObj.getComponent(PhysXComponent)!.kinematic = true;
            setTimeout(() => {
                this.player.setPositionWorld([0.1, 0.23, 1.15]);
                this.player.setRotationWorld([0, 1, 0, 0]);
                setTimeout(() => {
                    phyxObj.getComponent(PhysXComponent)!.kinematic = false;
                }, 100);
            });
        }
    }

    update(delta: number): void {
        this.isPlayerAlone = !(
            this.player!.getComponent(PaintballClient)!.remoteUsers.size > 0
        );
        this.targetShots = TargetShootingPanel.targetShots;
        this.userShots = TargetShootingPanel.userShots;
        this.timer = TargetShootingPanel.timer;

        if (this.targetShootingInProgress) {
            // Decrement the timer
            if (this.timer > 0) {
                this.timer -= delta; // Subtract elapsed time
                TargetShootingPanel.timer = this.timer; // Sync static timer value

                // Update the timer display
                this.timerTextComp!.text = 'Time: ' + Math.ceil(this.timer) + 's';
            } else {
                // Timer ran out, stop the game
                this.timer = 0;
                this.timerTextComp!.text = 'Time: 0s';
                this.player.setPositionWorld([0.1, 0.23, 1.15]);
                this.player.setRotationWorld([0, 1, 0, 0]);

                this.audioYouLoose.play();
                this.resetPlayer();
                this.stopTargetShooting();
            }
            //update target shots on score
            this.scoreTextComp!.text = this.targetShots + '/' + this.score;

            //calculate accuracy targetshots/usershot *100
            const accuracyRatio = this.targetShots / this.userShots || 0;
            const accuracy = accuracyRatio * 100;
            this.accuracyTextComp!.text = 'Accuracy : ' + Math.ceil(accuracy) + '%';
            //update accuracy text

            if (this.targetShots == this.score) {
                //play victory sound
                this.audioYouWin.play();
                this.resetPlayer();
                this.stopTargetShooting();
            }
        }
    }
}
